import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import cn from "classnames";
import { Select } from "antd";

const SIZE_OPTIONS = [
  {
    label:"10 / page",
    value: 10
  },
  {
    label:"20 / page",
    value: 20
  },
  {
    label:"50 / page",
    value: 50
  },
  {
    label:"100 / page",
    value: 100
  }
];

const Pagination = ({ total, current: pageFromParent = 1, pageSize: defaultPageSize = 10, hideOnSinglePage = true, showSizeChanger = true, onPageChange = _.noop, size }) => {
  
  const [current, setCurrent] = useState(pageFromParent);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  const totalPages = Math.ceil(total / pageSize);
  const maxNumberOfButtonsToShow = Math.min(7, totalPages);
  
  let startPageNumber = Math.max(current - 3, 1);
  const endPageNumber = Math.min(totalPages, startPageNumber + maxNumberOfButtonsToShow - 1);
  
  if (endPageNumber - current < 5) {
    startPageNumber = Math.max(1, endPageNumber - maxNumberOfButtonsToShow + 1)
  }
  
  useEffect(() => {
    setCurrent(pageFromParent);
  }, [pageFromParent]);

  useEffect(() => {
    setPageSize(defaultPageSize);
  }, [defaultPageSize]);

  const onPageClick = useCallback((page) => {
    if(onPageChange){
      onPageChange({
        current: page,
        pageSize
      });
    } else {
      setCurrent(page);
    }
  }, [onPageChange, pageSize]);
  
  const onSizeChange = useCallback((size) => {
    setPageSize(size);
    setCurrent(1);
    onPageChange({
      current: 1,
      pageSize: size
    });
  }, [onPageChange]);

  const pageArray = useMemo(() => {
    const pageArray = [];
    for (let i = startPageNumber; i <= endPageNumber; i++) {
      if (current === i) {
        pageArray.push(<Page key={i} className={"ant-pagination-item-active"} number={i}/>);
      } else {
        pageArray.push(<Page key={i} className={""} number={i} onPageClick={onPageClick} />);
      }
  
    }

    return pageArray;
  }, [current, endPageNumber, onPageClick, startPageNumber]);

  const isFirstPage = startPageNumber === current;
  const isLastPage = endPageNumber === current;
  const onlyPage = isFirstPage && isLastPage;

  const onNextClick = useCallback(() => {
    if(onPageChange){
      onPageChange({
        current: current + 1,
        pageSize
      });
    } else {
      setCurrent(current + 1);
    }
  }, [current, onPageChange, pageSize]);

  const onPrevClick = useCallback(() => {
    if(onPageChange){
      onPageChange({
        current: current - 1,
        pageSize
      });
    } else {
      setCurrent(current - 1);
    }
  }, [current, onPageChange, pageSize]);

  if ((hideOnSinglePage && onlyPage) || pageArray.length === 0) {
    return null;
  }

  return <ul className={cn("custom-pagination ant-table-pagination ant-pagination w-100 ant-table-pagination-right m-b-0", { mini: size === 'small' })}>
    <li onClick={isFirstPage ? _.noop : onPrevClick} title="Previous Page" className={cn("ant-pagination-prev", { "ant-pagination-disabled": isFirstPage})}>
      <button className="ant-pagination-item-link" type="button" disabled={isFirstPage}>
        <span className="anticon anticon-left">
          <svg viewBox="64 64 896 896" focusable="false" data-icon="left" width="1em" height="1em" fill="currentColor" aria-hidden="true"><path d="M724 218.3V141c0-6.7-7.7-10.4-12.9-6.3L260.3 486.8a31.86 31.86 0 000 50.3l450.8 352.1c5.3 4.1 12.9.4 12.9-6.3v-77.3c0-4.9-2.3-9.6-6.1-12.6l-360-281 360-281.1c3.8-3 6.1-7.7 6.1-12.6z" /></svg>
        </span>
      </button>
    </li>
    {
      _.map(pageArray, entry => entry)
    }
    <li onClick={isLastPage ? _.noop : onNextClick} title="Next Page" className={cn("ant-pagination-next", { "ant-pagination-disabled": isLastPage })}>
      <button className="ant-pagination-item-link" type="button" disabled={isLastPage}>
        <span className="anticon anticon-right">
          <svg viewBox="64 64 896 896" focusable="false" data-icon="right" width="1em" height="1em" fill="currentColor" aria-hidden="true"><path d="M765.7 486.8L314.9 134.7A7.97 7.97 0 00302 141v77.3c0 4.9 2.3 9.6 6.1 12.6l360 281.1-360 281.1c-3.9 3-6.1 7.7-6.1 12.6V883c0 6.7 7.7 10.4 12.9 6.3l450.8-352.1a31.96 31.96 0 000-50.4z" /></svg>
        </span>
      </button>
    </li>
    {showSizeChanger && <li className="ant-pagination-options">
      <Select options={SIZE_OPTIONS} value={pageSize} onChange={onSizeChange}/>
    </li>}
  </ul>;
};

export default Pagination;

const Page = ({ className, number, onPageClick = _.noop }) => {
  const onClick = useCallback(() => {
    onPageClick(number);
  }, [number, onPageClick]);

  return <li key={number} onClick={onClick} title={number} className={cn("ant-pagination-item", className)}>
    {number}
  </li>
};
